import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  Signal,
  signal,
} from '@angular/core';
import { SystemThemeService } from '../system-theme/system-theme.service';
import { PRIMENG_DARK_MODE_CSS_CLASS } from './padspin-primeng-dark-mode-selector';
import { ClassListChangeService } from '../class-list-change-service/class-list-change.service';

/**
 * Watches the system color scheme and toggles PrimeNG dark mode accordingly
 */
@Injectable({
  providedIn: 'root',
})
export class PrimengDarkModeTogglerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT, { optional: true });
  private readonly appRef = inject(ApplicationRef, { optional: true });
  private readonly sts = inject(SystemThemeService);
  private readonly clcs = inject(ClassListChangeService);

  private isEnabled = signal<boolean>(true);
  private readonly isSystemDark = computed(() => this.sts.theme() === 'dark');
  private readonly rootElementSignal: Signal<HTMLElement | undefined> = signal(
    this.document?.documentElement,
  );
  private readonly rootElementClassList = computed(() => {
    const rootElement = this.rootElementSignal();
    return rootElement
      ? this.clcs.getClassListSignal(rootElement)()
      : undefined;
  });

  /**
   * Add or remove the PRIMENG_DARK_MODE_CSS_CLASS class on the `<html>` element.
   */
  private _addOrRemoveCSSClass = effect(async () => {
    const isDark = this.isSystemDark();
    const rootElement = this.rootElementSignal();
    // We rely on the classList signal to re-run this effect when the
    // classList changes because sometimes the classlist is changed
    // and the dark mode class is removed by other code.
    const _rootElClassList = this.rootElementClassList();
    if (!this.isEnabled()) {
      return;
    }
    // On the server we don't know what the user's system theme is
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    // If we have a bug and the app never stabilizes, we don't want to wait forever
    await Promise.race([
      this.appRef?.whenStable(),
      new Promise((resolve) => setTimeout(resolve, 100)),
    ]);
    if (!rootElement) {
      return;
    }
    if (isDark) {
      rootElement.classList.add(PRIMENG_DARK_MODE_CSS_CLASS);
    } else {
      rootElement.classList.remove(PRIMENG_DARK_MODE_CSS_CLASS);
    }
  });
}
